import {
  incomeForecast,
  expensesForecast,
  cashflowForecast,
  assetsForecast,
  liabilitiesForecast,
} from '../forecast';
import PrepareForecast from '../forecast/prepareForecast';
import default_weights from '../forecast/weights';

export default class ForecastEngine {
  constructor(newData) {
    console.log('Initializing Forecast Engine');
    this.isReady = false;
    if (newData) this.setState(newData);
    this.id = 0;
  }

  compareAndUpdateProperty(propertyName, newValue) {
    if (this[propertyName] !== newValue) {
      this[propertyName] = newValue;
      return propertyName;
    }
    return null;
  }

  setState(params) {
    const changedProperties = Object.keys(params).map((propertyName) =>
      this.compareAndUpdateProperty(propertyName, params[propertyName])
    );

    changedProperties.filter(Boolean); // Remove null values

    this.id++;

    const isChanged = changedProperties.length > 0;
    if (isChanged) {
      console.log('🌦️ Running forecast... Changed:', changedProperties.filter((v) => v).join(', '));
      this.stale = true;
    }
  }

  isDataValid() {
    if (!this.fullRange) throw new Error('Missing full range data');
    if (!this.taxRateRetention) throw new Error('Missing full range data');
    if (!this.profile) throw new Error('Missing profile data');
    if (!this.expenseData) throw new Error('Missing expenseData data');
    if (!this.incomeData) throw new Error('Missing incomeData data');
    if (!this.period) throw new Error('Missing period data');
    if (!this.debouncedRange) throw new Error('Missing debouncedRange data');
    if (!this.userAssets) throw new Error('Missing userAssets data');
    if (!this.debts) throw new Error('Missing debts data');
    if (!this.accountsSnapshots) throw new Error('Missing accountsSnapshots data');
    if (!this.budgetSnapshots) throw new Error('Missing budgetSnapshots data');
    if (!this.annualInflationRate) throw new Error('Missing annualInflationRate data');
    if (!this.patches) throw new Error('Missing (budget) patches data');
    if (!this.accounts) throw new Error('Missing accounts data');
    if (!this.targetRetirementAge) throw new Error('Missing targetRetirementAge data');

    // If all required properties exist, set isReady to true
    this.isReady = true;
    return true;
  }

  async runForecasts(newData) {
    if (newData) this.setState(newData);

    const {
      fullRange,
      period,
      debouncedRange,
      userAssets, // investments,
      accountsSnapshots,
      incomeData,
      budgetSnapshots,
      patches,
      expenseData,
      debts,
      accounts,
      averageEquityGrowthRate,
      averageRealEstateGrowthRate,
      averageIncomeGrowthRate,
      propertyValue,
      annualInflationRate,
      averageEquityWithdrawalRate,
      profile,
      targetRetirementAge,
      averageRetirementEquityWithdrawalRate,
      socialSecurityIncome,
      birthdate,
      privateAssetsValue,
      averagePrivateAssetGrowthRate,
      taxRateRetention,
      assetSnapshots,
    } = this;

    if (!this.isDataValid()) throw new Error('Some data is still missing...');
    if (!this.stale) throw new Error('Already Forecasted data');

    const { budgets } = PrepareForecast({
      period,
      debouncedRange,
      expenseData,
      patches,
      invertValue: false,
      fullRange,
      birthdate,
      targetRetirementAge,
      weights: default_weights,
    });

    const parameters = {
      period,
      debouncedRange,
      dateRange: debouncedRange,
      userAssets, // investments,
      accountsSnapshots,
      accounts,
      averageEquityGrowthRate,
      averageRealEstateGrowthRate,
      propertyValue,
      fullRange,
      debts,
      expenseData,
      incomeData,
      patches,
      budgetSnapshots,
      budgets,
      averageEquityWithdrawalRate,
      profile,
      annualInflationRate,
      averageIncomeGrowthRate,
      targetRetirementAge,
      averageRetirementEquityWithdrawalRate,
      socialSecurityIncome,
      birthdate,
      privateAssetsValue,
      averagePrivateAssetGrowthRate,
      taxRateRetention,
      assetSnapshots,
      weights: default_weights,
    };

    const assets = assetsForecast({ ...parameters, cashflow: this.forecast?.cashflow });
    const liabilities = liabilitiesForecast(parameters);
    const income = incomeForecast({ ...parameters, assets });
    const expenses = expensesForecast({ ...parameters, liabilities });
    const cashflow = cashflowForecast(period, income, expenses, profile);
    // kinda hacky, but we'll run the assets forecast once more with the cashflow
    const assetsWithCashflow = assetsForecast({ ...parameters, cashflow, income });

    this.forecast = { assets: assetsWithCashflow, income, liabilities, expenses, cashflow };
    // console.log({ forecast: this.forecast });

    return this.forecast;
  }
}

/*

Example usage:

const engine = new ExpenseEngine(period, dateRange, data, budgetSnapshots);
const forecastedData = engine.calculateForecast();
console.log(forecastedData);

*/
